import { EXPENSE_CATEGORIES } from '../../utils/categories';
import { LIMITS } from '../../utils/validation';
import FormField, { ValidatedInput } from '../UI/FormField';
import Select from '../UI/Select';
import DatePicker from '../UI/DatePicker';

// Expense category options for Select
const EXPENSE_CATEGORY_OPTIONS = EXPENSE_CATEGORIES.map(cat => ({
  value: cat.value,
  label: cat.label,
}));

// Currency options
const CURRENCY_OPTIONS = [
  { value: 'EUR', label: 'EUR' },
  { value: 'GBP', label: 'GBP' },
  { value: 'USD', label: 'USD' },
];

/**
 * ExpenseForm - Form fields for creating an expense
 * Can be standalone or combined with an event
 */
export default function ExpenseForm({
  expenseData,
  onChange,
  errors = {},
  entryType, // 'expense' or 'both'
  showDivider = false,
  disabled = false,
}) {
  function handleChange(field, value) {
    onChange({ ...expenseData, [field]: value });
  }

  return (
    <>
      {/* Divider when combined with event */}
      {showDivider && (
        <div 
          className="pt-4 mt-4"
          style={{ borderTop: '1px solid var(--color-border-subtle)' }}
        >
          <h3 
            className="font-medium mb-3" 
            style={{ color: 'var(--color-text-primary)' }}
          >
            Expense Details
          </h3>
        </div>
      )}

      {/* Description (only for standalone expenses) */}
      {entryType === 'expense' && (
        <FormField label="Description" required error={errors.description}>
          <ValidatedInput
            type="text"
            value={expenseData.description}
            onChange={(e) => handleChange('description', e.target.value)}
            error={errors.description}
            placeholder="Airport transfer"
            maxLength={LIMITS.DESCRIPTION_MAX}
            disabled={disabled}
          />
        </FormField>
      )}

      {/* Category */}
      <FormField label="Expense Category" required error={errors.category}>
        <Select
          options={EXPENSE_CATEGORY_OPTIONS}
          value={expenseData.category}
          onChange={(value) => handleChange('category', value)}
          error={errors.category}
          disabled={disabled}
        />
      </FormField>

      {/* Amount & Currency */}
      <div className="grid grid-cols-2 gap-4">
        <FormField label="Amount" required error={errors.amount}>
          <ValidatedInput
            type="number"
            value={expenseData.amount}
            onChange={(e) => handleChange('amount', e.target.value)}
            error={errors.amount}
            placeholder="0.00"
            min="0"
            max={LIMITS.AMOUNT_MAX}
            step="0.01"
            disabled={disabled}
          />
        </FormField>
        <FormField label="Currency" error={errors.currency}>
          <Select
            options={CURRENCY_OPTIONS}
            value={expenseData.currency}
            onChange={(value) => handleChange('currency', value)}
            error={errors.currency}
            disabled={disabled}
          />
        </FormField>
      </div>

      {/* Planned vs Actual */}
      <div className="flex items-center gap-4">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="radio"
            name="expense_type"
            checked={expenseData.is_planned}
            onChange={() => handleChange('is_planned', true)}
            className="w-4 h-4"
            style={{ accentColor: 'var(--color-brand-primary)' }}
            disabled={disabled}
          />
          <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
            Planned/Budgeted
          </span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="radio"
            name="expense_type"
            checked={!expenseData.is_planned}
            onChange={() => handleChange('is_planned', false)}
            className="w-4 h-4"
            style={{ accentColor: 'var(--color-brand-primary)' }}
            disabled={disabled}
          />
          <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
            Actual Expense
          </span>
        </label>
      </div>

      {/* Date (only for standalone expenses) */}
      {entryType === 'expense' && (
        <FormField label="Date" required error={errors.expense_date}>
          <DatePicker
            value={expenseData.expense_date}
            onChange={(value) => handleChange('expense_date', value)}
            error={errors.expense_date}
            disabled={disabled}
          />
        </FormField>
      )}
    </>
  );
}
